import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Searchbar } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Container from "../Container";
import type { Lesson } from "../LessonsHomeScreen/types";
import FilterModalContainer from "../Library/FilterModalContainer";
import SortModalContainer from "../Library/SortModalContainer";
import Loader from "../Loader";
import { LessonsExploreHeader } from "./LessonsExploreHeader";
import { LessonsExploreList } from "./LessonsExploreList";
import { TopicChipsRow } from "./TopicChipsRow";
import type {
	ExploreChip,
	ExploreFilterChipType,
	ExploreFilterState,
	LessonSection,
	LessonSortOption,
	LessonSortType,
	SortOrder,
	TopicFilter,
} from "./types";

type Props = {
	searchQuery: string;
	onChangeSearch: (query: string) => void;
	filterState: ExploreFilterState;
	onToggleFilter: (type: Exclude<ExploreFilterChipType, "topics">) => void;
	sections: LessonSection[];
	totalLessons: number;
	onLessonPress: (lesson: Lesson) => void;
	sortOptions?: LessonSortOption[];
	sortType: LessonSortType;
	sortOrder: SortOrder;
	onSelectSort: (type: string) => void;
	topicFilters: TopicFilter[];
	onApplyTopicFilters: (filters: TopicFilter[]) => void;
	onClearFilters: () => void;
	sortModalTitle?: string;
	filterModalTitle?: string;
	isLoading?: boolean;
};

export const LessonsExploreScreen = ({
	searchQuery,
	onChangeSearch,
	filterState,
	onToggleFilter,
	sections,
	totalLessons,
	onLessonPress,
	sortOptions,
	sortType,
	sortOrder,
	onSelectSort,
	topicFilters,
	onApplyTopicFilters,
	onClearFilters,
	sortModalTitle,
	filterModalTitle,
	isLoading = false,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const [showSortModal, setShowSortModal] = useState(false);
	const [showFilterModal, setShowFilterModal] = useState(false);

	const numLessons = useMemo(
		() => sections.reduce((acc, section) => acc + section.data.length, 0),
		[sections],
	);

	const resolvedSortOptions = useMemo<LessonSortOption[]>(
		() =>
			sortOptions ?? [
				{
					label: t("lessons.explore.sort.popularity"),
					icon: "chart-line-variant",
					type: "popular",
					buttonLabel: t("lessons.explore.sort.popularityShort"),
				},
				{
					label: t("lessons.explore.sort.topic"),
					icon: "stethoscope",
					type: "speciality",
					buttonLabel: t("lessons.explore.sort.topic"),
				},
				{
					label: t("lessons.explore.sort.difficulty"),
					icon: "weight-lifter",
					type: "difficulty",
					buttonLabel: t("lessons.explore.sort.difficulty"),
				},
				{
					label: t("lessons.explore.sort.recent"),
					icon: "publish",
					type: "recent",
					buttonLabel: t("lessons.explore.sort.recentShort"),
				},
				{
					label: t("lessons.explore.sort.completed"),
					icon: "check-circle-outline",
					type: "completed",
					buttonLabel: t("lessons.explore.sort.completed"),
				},
			],
		[sortOptions, t],
	);

	const chips = useMemo<ExploreChip[]>(() => {
		const { isRecentSelected, isCompletedSelected, isPremiumSelected } =
			filterState;
		const { selectedTopics } = filterState;
		const isAllSelected =
			!(isRecentSelected || isCompletedSelected || isPremiumSelected) &&
			selectedTopics.length === 0;
		const topicsLabel =
			selectedTopics.length === 0
				? t("lessons.explore.chips.topics")
				: selectedTopics.length === 1
					? selectedTopics[0]
					: t("lessons.explore.chips.topicsCount", {
							count: selectedTopics.length,
						});

		return [
			{
				label: t("lessons.explore.chips.all"),
				type: "all",
				tooltip: t("lessons.explore.chipTooltips.all"),
				selected: isAllSelected,
			},
			{
				label: t("lessons.explore.chips.recent"),
				type: "recent",
				tooltip: t("lessons.explore.chipTooltips.recent"),
				selected: isRecentSelected,
			},
			{
				label: t("lessons.explore.chips.completed"),
				type: "completed",
				tooltip: t("lessons.explore.chipTooltips.completed"),
				selected: isCompletedSelected,
			},
			{
				label: t("lessons.explore.chips.premium"),
				type: "premium",
				tooltip: t("lessons.explore.chipTooltips.premium"),
				selected: isPremiumSelected,
			},
			{
				label: topicsLabel,
				type: "topics",
				tooltip: t("lessons.explore.chipTooltips.topics"),
				selected: selectedTopics.length > 0,
			},
		];
	}, [filterState, t]);

	const handleChipPress = useCallback(
		(type: ExploreChip["type"]) => {
			if (type === "topics") {
				setShowFilterModal(true);
				return;
			}
			onToggleFilter(type);
		},
		[onToggleFilter],
	);

	const handleSelectSort = useCallback(
		(type: string) => {
			onSelectSort(type);
			setShowSortModal(false);
		},
		[onSelectSort],
	);

	const searchPlaceholder = t("lessons.explore.searchPlaceholder");

	return (
		<Box flex={1}>
			{isMobile ? (
				<Box paddingHorizontal={16} paddingTop={12} paddingBottom={12}>
					<Searchbar
						mode="bar"
						placeholder={searchPlaceholder}
						value={searchQuery}
						onChangeText={onChangeSearch}
						placeholderTextColor={theme.colors.onSurfaceVariant}
					/>
				</Box>
			) : (
				<Box>
					<Container maxWidth="sm">
						<Box paddingTop={16} paddingBottom={16} width="100%">
							<Searchbar
								placeholder={searchPlaceholder}
								value={searchQuery}
								onChangeText={onChangeSearch}
								style={{
									backgroundColor: theme.colors.elevation.level3,
									borderRadius: theme.roundness * 100,
								}}
							/>
						</Box>
						<Box paddingBottom={12} width="100%">
							<TopicChipsRow chips={chips} onChipPress={handleChipPress} />
						</Box>
					</Container>
				</Box>
			)}
			{isLoading ? (
				<Loader />
			) : (
				<Box flex={1} width="100%">
					<Container maxWidth="sm" autoSize>
						<LessonsExploreHeader
							numLessons={numLessons}
							chips={chips}
							onChipPress={handleChipPress}
							searchQuery={searchQuery}
							sortOptions={resolvedSortOptions}
							sortType={sortType}
							sortOrder={sortOrder}
							onSortPress={() => setShowSortModal(true)}
							hideChips={!isMobile}
						/>
					</Container>
					<Box paddingBottom={8} flex={1} width="100%">
						<LessonsExploreList
							sections={sections}
							onLessonPress={onLessonPress}
							onClearFilters={onClearFilters}
							totalLessons={totalLessons}
							searchQuery={searchQuery}
							filterState={filterState}
							contentContainerStyle={{
								paddingBottom: 200,
								paddingHorizontal: isMobile ? 16 : 0,
							}}
						/>
					</Box>
				</Box>
			)}
			<SortModalContainer
				showModal={showSortModal}
				handleSheetClose={() => setShowSortModal(false)}
				handleSelectSortItem={handleSelectSort}
				sortItems={resolvedSortOptions}
				currentSortType={sortType}
				title={sortModalTitle ?? t("lessons.explore.sortModalTitle")}
				icon="sort-variant"
				presentation={isMobile ? "sheet" : "dialog"}
			/>
			<FilterModalContainer
				showModal={showFilterModal}
				handleSheetClose={() => setShowFilterModal(false)}
				specialitiesFilter={topicFilters}
				applyFilters={onApplyTopicFilters}
				title={filterModalTitle ?? t("lessons.explore.filterModalTitle")}
				icon="filter-variant"
				presentation={isMobile ? "sheet" : "dialog"}
			/>
		</Box>
	);
};
